import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, FileText, Users, Building, Trash2, Bus } from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { NEWS_DATA } from '@/data/news';
import { EVENTS_DATA } from '@/data/events';
import { NewsCard, EventCard, ServiceCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const SERVICES_QUICK = [
  {
    id: 'etat-civil',
    title: 'État civil',
    description: 'Actes de naissance, mariage, décès',
    icon: FileText,
  },
  {
    id: 'urbanisme',
    title: 'Urbanisme',
    description: 'Permis de construire, travaux',
    icon: Building,
  },
  {
    id: 'portail-famille',
    title: 'Portail Famille',
    description: 'Inscriptions, cantine, activités',
    icon: Users,
  },
  {
    id: 'dechets',
    title: 'Déchets',
    description: 'Collecte, déchetterie, broyage',
    icon: Trash2,
  },
];

const STATS = [
  { label: 'Habitants', value: '4 200' },
  { label: 'Superficie', value: '13 km²' },
  { label: 'Associations', value: '45+' },
  { label: 'Écoles', value: '3' },
];

export default function Home() {
  const featuredNews = NEWS_DATA.find(n => n.featured) || NEWS_DATA[0];
  const recentNews = NEWS_DATA.filter(n => !n.featured).slice(0, 3);
  const upcomingEvents = EVENTS_DATA.slice(0, 4);

  return (
    <div className="min-h-screen">
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.VILLAGE_3}
            alt="Gréasque"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="relative z-10 text-center px-4 max-w-4xl mx-auto"
        >
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Bienvenue à <span className="text-primary">Gréasque</span>
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Une commune provençale au cœur des Bouches-du-Rhône, riche de son patrimoine minier et de sa qualité de vie
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="text-lg">
              <Link to={ROUTE_PATHS.ACTUALITES}>
                Actualités <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg">
              <Link to={ROUTE_PATHS.LA_VILLE}>Découvrir la ville</Link>
            </Button>
          </div>
        </motion.div>
      </section>

      <section className="py-24 px-4 bg-muted/30">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Actualités</h2>
            <p className="text-xl text-muted-foreground">Restez informé de la vie municipale</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <NewsCard news={featuredNews} featured />
            </motion.div>

            <div className="grid gap-6">
              {recentNews.map((news, index) => (
                <motion.div
                  key={news.id}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                >
                  <NewsCard news={news} />
                </motion.div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <Button asChild variant="outline" size="lg">
              <Link to={ROUTE_PATHS.ACTUALITES}>
                Toutes les actualités <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Événements à venir</h2>
            <p className="text-xl text-muted-foreground">Participez à la vie locale</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {upcomingEvents.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <EventCard event={event} />
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Button asChild variant="outline" size="lg">
              <Link to={ROUTE_PATHS.AGENDA}>
                <Calendar className="mr-2 h-4 w-4" />
                Voir l'agenda complet
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 bg-muted/30">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Services rapides</h2>
            <p className="text-xl text-muted-foreground">Accédez facilement à vos démarches</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES_QUICK.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="p-6 text-center hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer group">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{service.title}</h3>
                    <p className="text-sm text-muted-foreground">{service.description}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg">
              <Link to={ROUTE_PATHS.VIE_PRATIQUE}>Tous les services</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Gréasque en chiffres</h2>
            <p className="text-xl text-muted-foreground">Notre commune en quelques données</p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {STATS.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-5xl md:text-6xl font-bold text-primary mb-2">
                  {stat.value}
                </div>
                <div className="text-lg text-muted-foreground">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 bg-primary/5">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Restez connecté</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Suivez l'actualité de Gréasque et ne manquez aucun événement
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <Link to={ROUTE_PATHS.CONTACT}>Nous contacter</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to={ROUTE_PATHS.PUBLICATIONS}>Publications municipales</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}